import * as R from 'ramda';

export const CPA_BUTTON_NAME = {
  SIGN_UP: 'CpaSignUp',
  BACK_TO_LUMIQ: 'BackToLumiq',
  CONTENT_END_MODAL: 'CpaContentEndModal',
  SIGNUP_MODAL: 'CpaSignUpModal',
  CONTENT_VERIFIABLE_MODAL: 'CpaContentVerifiableModal',
  CANADA_LOGO: 'CpaCanadaLogo',
};

const createAsyncApiEvents = (event: string) => ({
  INTENT: `${event}.intent`,
  SUCCESS: `${event}.success`,
  FAIL: `${event}.fail`,
});

const AUTH_EVENTS = {
  ...R.map(createAsyncApiEvents, {
    LOGIN: 'accounts.login',
    REGISTER: 'accounts.register',
    SEND_PASSWORD_RESET_EMAIL: 'password.send_reset_email',
    PASSWORD_RESET: 'password.reset',
    EMAIL_VALIDATION: 'accounts.email.validation',
  }),
  SSO_REDIRECT: 'accounts.sso.redirect',
  REGISTER_STEP: 'accounts.register.step',
  REGISTER_EMAIL_PREFILLED: 'accounts.register.email_prefilled',
};

const ACCOUNT_RECOVERY_EVENTS = {
  ...R.map(createAsyncApiEvents, {
    ADD_RECOVERY_EMAIL: 'accounts.recovery_email.add',
  }),
  RECOVERY_STEP: 'accounts.recovery.step',
};

const ASSESSMENT_API_EVENTS = {
  SUBMIT: 'assessment.submit',
};

const ASSESSMENT_EVENTS = {
  ATTEMPT: 'assessment.attempt',
  CLOSE: 'assessment.close',
  PASS: 'assessment.pass',
  FAIL: 'assessment.fail',
  QUESTION_NAVIGATE: 'assessment.question_navigate',
  QUESTION_ANSWERED: 'assessment.question_answered',
  LOCKED: 'assessment.locked_view',
  RETAKE: 'assessment.retake',
  ...R.map(createAsyncApiEvents, ASSESSMENT_API_EVENTS),
};

const CAROUSEL_EVENTS = {
  SCROLL: 'carousel.scroll',
  SWITCH_TAB: 'carousel.switch_tab',
  DISMISSED: 'carousel.dismissed',
};

const CHAPTER_EVENTS = {
  START: 'chapter.start',
  COMPLETE: 'chapter.complete',
  PLAY: 'chapter.play',
  SEEK: 'chapter.seek',
  PAUSE: 'chapter.pause',
  RESUME: 'chapter.resume',
  PLAYBACK_SPEED_CHANGED: 'chapter.playback_speed_changed',
};

const EPISODE_EVENTS = {
  IMPRESSION: 'episode.impression',
  HOVER: 'episode.hover',
  MEANINGFUL_HOVER: 'episode.meaningful_hover',
  TRAILER_PREVIEW_START: 'episode.trailer_preview_start',
  TRAILER_PREVIEW_END: 'episode.trailer_preview_end',
  TRAILER_PREVIEW_UNMUTE: 'episode.trailer_preview_unmute',
  TRAILER_PREVIEW_MUTE: 'episode.trailer_preview_mute',
  START: 'episode.start',
  LISTENED: 'episode.listened',
  COMPLETE: 'episode.complete',
  BOOKMARK: 'episode.bookmark',
  REDEEM: 'episode.redeem',
};

const EPISODE_DETAIL_EVENTS = {
  SWITCH_TAB: 'episode_detail.switch_tab',
};

const EPISODE_FEEDBACK_EVENTS = {
  FEEDBACK: 'episode_feedback',
  EVALUATION: 'episode_evaluation',
};

const FILTER_LIBRARY_EVENTS = {
  FILTER: 'filter_library.filter',
  VIEW_EPISODE: 'filter_library.view_episode',
  FILTER_REMOVE: 'filter_library.filter_remove',
  FILTER_CLEAR: 'filter_library.filter_clear',
};

const LEARNING_PATH_EVENTS = {
  IMPRESSION: 'learning_path.impression',
  CARD_CLICKED: 'learning_path.card_clicked',
  SIDEBAR_ITEM_CLICKED: 'learning_path.sidebar_item_clicked',
  PAGE_VIEWED: 'learning_path.page_viewed',
  VIEW_MODE_TOGGLED: 'learning_path.view_mode_toggled',
  VIEW_ALL_CLICKED: 'learning_path.view_all_clicked',
};

const MEMBER_MANAGEMENT_API_EVENTS = {
  REMOVE: 'member_management.remove',
  RESEND: 'member_management.resend',
  ADD_MEMBERS: 'member_management.add_members',
  EDIT_MEMBER: 'member_management.edit_member',
};
const MEMBER_MANAGEMENT_EVENTS = {
  REMOVE_MEMBER: 'member_management.remove_member',
  RESEND_INVITE: 'member_management.resend_invite',
  DOWNLOAD_CSV: 'member_management.download_csv',
  ...R.map(createAsyncApiEvents, MEMBER_MANAGEMENT_API_EVENTS),
};

const MODAL_EVENTS = {
  OPEN: 'modal.open',
  CLOSE: 'modal.close',
  PAGE_VIEW: 'modal.page_view',
  PAGE_TOGGLE: 'modal.page_toggle',
  PAGE_COMPLETE: 'modal.page_complete',
};

const ONBOARDING_API_EVENTS = {
  UPDATE_DESIGNATION: 'onboarding.update_designation',
  UPDATE_PROFILE: 'onboarding.update_profile',
  UPDATE_JOB_DETAILS: 'onboarding.update_job_details',
  RESEND_B2B: 'onboarding.resend_b2b_invitation',
  FINALIZE: 'onboarding.finalize',
};

const ONBOARDING_EVENTS = {
  START: 'onboarding.start',
  STEP: 'onboarding.step',
  STEP_SUBMIT: 'onboarding.step_submit',
  FINISH: 'onboarding.finish',
  ...R.map(createAsyncApiEvents, ONBOARDING_API_EVENTS),
};

const GET_STARTED_CHECKLIST_EVENTS = {
  VIEW_COLLAPSE: 'get_started_checklist.view_collapse',
  VIEW_EXPAND: 'get_started_checklist.view_expand',
  STEP: 'get_started_checklist.step',
  CTA_CLICK: 'get_started_checklist.cta_click',
  FINISH: 'get_started_checklist.finish',
};

const PAGE_EVENTS = {
  LOAD: 'page.load',
  VIEW: 'page.view',
  VIEW_EPISODE: 'page.view_episode',
  VIEW_PRICING: 'page.view_pricing', // Note: Represents user clicking a CTA that leads to the pricing page, not landing on the pricing page directly
};

const PROFILE_API_EVENTS = {
  UPDATE_DETAILS: 'profile.update_details',
  UPDATE_EMAIL: 'profile.update_email',
  UPDATE_PASSWORD: 'profile.update_password',
};

const PROFILE_EVENTS = {
  ...R.map(createAsyncApiEvents, PROFILE_API_EVENTS),
};

const PRODUCT_FEEDBACK_EVENTS = {
  ...R.map(createAsyncApiEvents, {
    SUBMIT: 'product_feedback.submit',
  }),
  FEEDBACK: 'product_feedback',
  TRUSTPILOT: 'product_feedback.trustpilot_cta_shown',
};

const QUIZ_EVENTS = {
  ATTEMPT: 'quiz.attempt',
  PASS: 'quiz.pass',
  FAIL: 'quiz.fail',
  VIEW: 'quiz.view',
};

const POST_QUIZ_RECOMMENDATIONS_EVENTS = {
  CLOSE: 'post_quiz_recommendations.close',
};

const REPORTING_EVENTS = {
  DOWNLOAD_CSV: 'reporting.download_csv',
  SEE_MEMBERS: 'reporting.see_members_clicked',
};

const SEARCH_API_EVENTS = {
  SUBMIT: 'search.submit',
};

const SEARCH_EVENTS = {
  ...R.map(createAsyncApiEvents, SEARCH_API_EVENTS),
  SET_FILTER: 'search.set_filter',
  SELECT_QUERY_SUGGESTION: 'search.select_query_suggestion',
};

const EMAIL_PREFERENCES_EVENTS = {
  TOGGLE_SUBSCRIPTION: 'email_preferences.toggle',
  UPDATE_SUBSCRIPTIONS: 'email_preferences.update_all',
  KEEP_SUBSCRIPTION: 'email_preferences.keep',
  CONFIRM_UNSUBSCRIBE: 'email_preferences.confirm_unsubscribe',
};

const TRACK_PLAYER_EVENTS = {
  ADJUST_VOLUME: 'track_player.adjust_volume',
  BOOKMARK: 'track_player.bookmark',
  CHANGE_POSITION: 'track_player.change_position',
  CHANGE_SPEED: 'track_player.change_speed',
  EPISODE_FEEDBACK: 'track_player.episode_feedback',
  MUTE: 'track_player.mute',
  PAUSE: 'track_player.pause',
  PLAY_RESUME: 'track_player.play_resume',
  REWIND: 'track_player.rewind',
  SKIP_CHAPTER: 'track_player.skip_chapter',
  VIEW_EPISODE_DETAILS: 'track_player.view_episode_details',
};

const VIDEO_PLAYER_EVENTS = {
  FULLSCREEN: 'video_player.fullscreen',
  PAUSE: 'video_player.pause',
  PLAY: 'video_player.play_resume',
};

const APP_LOGIN_MAGIC_LINK_EVENTS = {
  ...R.map(createAsyncApiEvents, {
    GENERATE: 'app_login_magic_link.generate',
  }),
  REGENERATE: 'app_login_magic_link.regenerate',
};

const FREE_TRIAL_SHARE_EVENTS = {
  DISMISSED: 'free_trial_share.dismissed',
  ...R.map(createAsyncApiEvents, {
    SHARE: 'free_trial_share.submit',
  }),
};

const EPISODE_SHARE_EVENTS = {
  LINK_COPY: 'episode_share.link_copy',
  ...R.map(createAsyncApiEvents, {
    EMAIL: 'episode_share.send_email',
  }),
};

const MARKETING_EVENTS = {
  SIGNUP_VIEWED: 'marketing.signup_viewed',
  START_FREE_TRIAL_CTA_CLICK: 'marketing.start_free_trial_cta_click',
  PLAY_TRAILER: 'marketing.play_trailer',
  VIEW_PRICING: 'marketing.view_pricing', // This can technically be inferred from the default page.view event, but is explicitly logged for ease of use
};

const TRACKER_EVENTS = {
  ...R.map(createAsyncApiEvents, {
    DOWNLOAD_CERTIFICATE: 'tracker.lumiq_certificate.download',
    DOWNLOAD_CERTIFICATE_SUMMARY: 'tracker.summary_report.download',
    CREATE_CUSTOM_CERTIFICATE: 'tracker.external_hours.create',
    UPDATE_CUSTOM_CERTIFICATE: 'tracker.external_hours.update',
    DELETE_CUSTOM_CERTIFICATE: 'tracker.external_hours.delete',
  }),

  TRIENNIAL_SUMMARY_TOGGLED: 'tracker.cycle_panel_toggled',
  TRIENNIAL_LINK_CLICKED: 'tracker.regulatory_body_link_clicked',

  SOURCE_FILTER_CHANGED: 'tracker.source_filter_changed',
  SEARCH_USED: 'tracker.search_used',
  SORT_CHANGED: 'tracker.sort_changed',

  CERTIFICATE_DOWNLOADED: 'tracker.certificate_downloaded',
  ADD_EXTERNAL_CERTIFICATE_CLICKED: 'tracker.add_external_certificate_clicked',
  EXTERNAL_CERTIFICATE_EDITED: 'tracker.external_certificate_edited',
  CERTIFICATES_EXPORTED: 'tracker.export',

  PINNED_FOS_EDIT: 'tracker.pinned_fos.edit',
  PINNED_FOS_DISMISS: 'tracker.pinned_fos.announcement_dismissed',
};

const CONTENT_CURATION_EVENTS = {
  LP_MESSAGE_DISMISSED: 'pinned_learning_paths_message.dismissed',
  LP_PIN: 'learning_path.pin',
  LP_UNPIN: 'learning_path.unpin',
  LP_REORDER: 'learning_path.reorder',
  RECOMMENDED_EPISODE_ADD: 'recommended_episodes.add_episode',
  RECOMMENDED_EPISODE_REORDER: 'recommended_episodes.reorder',
  RECOMMENDED_EPISODE_REMOVE: 'recommended_episodes.remove',
};

const TEAM_UPGRADE_EVENTS = {
  TEAM_UPGRADE_PROMPT_DISMISSED: 'team_upgrade_prompt.dismissed',
  TEAM_UPGRADE_CLICKED: 'team_upgrade.clicked',
  TEAM_UPGRADE_STARTED: 'team_upgrade.started',
};

const PRICING_EVENTS = {
  CTA_CLICKED: 'pricing.cta_clicked',
  REDIRECT_CHECKOUT_TO_BILLING_PORTAL:
    'pricing.redirect_checkout_to_billing_portal',
  CHECKOUT_STARTED: 'pricing.checkout_started',
  CHECKOUT_SUCCESS: 'pricing.checkout_success',
  CHECKOUT_FAILURE: 'pricing.checkout_failure',
  HDYHAU_DROPDOWN_SELECT: 'hdyhau.dropdown_select',
  HDYHAU_SUBMIT: 'hdyhau.submit',
  HDYHAU_ABORT: 'hdyhau.abort',
};

const LIBRARY_EVENTS = {
  RESET: 'library.clear_all_filters ',
  CATEGORY: 'library_category.selected',
  VIEW: 'library.viewed',
  MEDIA_TYPE: 'library_filter.media_type_changed',
  REGION: 'library_filter.region_changed',
  SORT: 'library_filter.sort_changed',
};

const CONTENT_PREFERENCES_EVENTS = {
  UPDATE_REGIONS: 'content_preferences.update_regions',
};

/**
 * Reference: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=17740408596496099019-NC&client_type=gtm
 */
const GA4_KEY_EVENTS = {
  PURCHASE: 'purchase',
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  SEARCH: 'search',
  BEGIN_CHECKOUT: 'begin_checkout',
};

const VISUAL_LEARNING_EVENTS = {
  OPEN_VISUAL_LEARNING: 'visual_learning.open_visual_learning',
  CLOSE_VISUAL_LEARNING: 'visual_learning.close_visual_learning',
  NEXT_PAGE: 'visual_learning.next_page',
  PREVIOUS_PAGE: 'visual_learning.previous_page',
  VISUAL_AID_HOVER: 'visual_learning.visual_aid_hover',
};

const DATE_RANGE_EVENTS = {
  OPENED: 'date_range.opened',
  APPLIED: 'date_range.applied',
  CANCELLED: 'date_range.cancelled',
};

export const EVENTS = {
  ASSESSMENT_EVENTS,
  AUTH_EVENTS,
  ACCOUNT_RECOVERY_EVENTS,
  CAROUSEL_EVENTS,
  CHAPTER_EVENTS,
  EPISODE_EVENTS,
  LEARNING_PATH_EVENTS,
  EPISODE_DETAIL_EVENTS,
  EPISODE_FEEDBACK_EVENTS,
  FILTER_LIBRARY_EVENTS,
  FREE_TRIAL_SHARE_EVENTS,
  EPISODE_SHARE_EVENTS,
  MODAL_EVENTS,
  ONBOARDING_EVENTS,
  GET_STARTED_CHECKLIST_EVENTS,
  PAGE_EVENTS,
  PROFILE_EVENTS,
  QUIZ_EVENTS,
  REPORTING_EVENTS,
  SEARCH_EVENTS,
  EMAIL_PREFERENCES_EVENTS,
  TRACKER_EVENTS,
  TRACK_PLAYER_EVENTS,
  VIDEO_PLAYER_EVENTS,
  PRODUCT_FEEDBACK_EVENTS,
  MEMBER_MANAGEMENT_EVENTS,
  APP_LOGIN_MAGIC_LINK_EVENTS,
  DEBUG: 'debug',
  VISUAL_AID_VIEW: 'visual_aid.view',
  BUTTON_CLICK: 'button.click',
  CLICK_THROUGH: 'click_through',
  RESOURCE_VIEW: 'resource.view',
  CHANGE_REGION: 'region.update',
  DATE_SELECT: 'date.select',
  POST_QUIZ_RECOMMENDATIONS_EVENTS,
  MARKETING_EVENTS,
  PRICING_EVENTS,
  GA4_KEY_EVENTS,
  CONTENT_CURATION_EVENTS,
  TEAM_UPGRADE_EVENTS,
  VISUAL_LEARNING_EVENTS,
  LIBRARY_EVENTS,
  CONTENT_PREFERENCES_EVENTS,
  DATE_RANGE_EVENTS,
};

// Location constants

const BUTTON = {
  ASSESSMENT: 'assessment',
  BOOKMARK: 'bookmark',
  EPISODE_DETAILS: 'episode details',
  PLAY: 'play',
  PLAY_CHAPTER: 'play chapter',
  PLAY_NOW: 'play now',
  PRICING: 'pricing',
  PREVIEW: 'preview',
  REDEEM: 'redeem',
  SCROLL_LEFT: 'scroll left',
  SCROLL_RIGHT: 'scroll right',
  SEEK_THIRTY: 'seek 30s back',
  SHARE: 'share',
  SKIP_NEXT: 'skip next',
  SKIP_PREVIOUS: 'skip previous',
  SPEED: 'change speed',
  TRAILER: 'trailer',
  REMOVE: 'remove',
  RESEND: 'resend',
  CONFIRM: 'confirm',
  DOWNLOAD_CSV: 'download csv',
  VISUAL_AID: 'visual aid',
  EPISODE_RESOURCE: 'episode resource',
  START_FREE_TRIAL: 'start free trial',
  DOWNLOAD_APP: 'download app',
  SEE_PRICING_DETAILS: 'see pricing details',
};

const COMPONENT = {
  BULK_EDIT_TOOLBAR: 'bulk edit toolbar',
  EDIT_MEMBER_MODAL: 'edit member modal',
  CHAPTER_QUIZ_MODAL: 'chapter/quiz modal',
  TRAILER_PREVIEW: 'trailer preview',
  EPISODE_CARD: 'episode card',
  EPISODE_BANNER: 'episode details banner',
  FEATURED_BANNER: 'featured banner',
  LEARNING_PATH_CARD: 'learning path card',
  LEARNING_PATH_EPISODE: 'learning path episode card',
  MESSAGE_BANNER: 'message banner',
  MINI_PLAYER: 'mini player',
  MODAL: 'modal',
  NAV_BAR: 'navigation bar',
  SIDE_BAR: 'sidebar',
  RELATED_EPISODE: 'related episode',
  SEARCH_ROW: 'search row',
  TABLE_ROW: 'table row',
  THREE_DOT_MENU: 'three-dot menu',
  TRACK_PLAYER: 'track player',
  VIDEO_PLAYER: 'video player',
  INLINE_SIGNUP_FORM: 'inline signup form',
  DELAYED_SIGNUP_PROMPT: 'delayed signup prompt',
  DATE_RANGE_FROM: 'date_range.from',
  DATE_RANGE_TO: 'date_range.to',
  ONBOARDING_CHECKLIST: 'onboarding checklist',
  PROFILE_PAGE_FORM: 'profile page form',
};

const PAGE = {
  CATEGORIES: 'categories',
  DISCOVER: 'discover',
  EPISODE_DETAILS: 'episode details',
  FILTER_LIBRARY: 'filter library',
  LEARNING_PATHS: 'learning paths',
  LEARNING_PATH_DETAILS: 'learning path details',
  MY_LUMIQ: 'my lumiq',
  PROFILE: 'profile',
  SEARCH: 'search',
  EMAIL_PREFERENCES: 'email preferences',
  TRACKER: 'tracker',
  ONBOARDING: 'onboarding',
  QUIZ_COMPLETE: 'quiz complete',
  ASSESSMENT_COMPLETE: 'assessment complete',
  MEMBER_MANAGEMENT: 'member management',
  CONTENT_CURATION: 'content curation',
  LOGIN: 'login',
  REGISTER: 'register',
  REPORTING: 'reporting',
};

const SECTION = {
  ASSESSMENTS: 'assessments',
  BOOKMARKED: 'bookmarked episodes',
  COMPLETED: 'completed',
  IN_PROGRESS: 'in progress',
  PLAN_INFO: 'plan info',
  QUIZZES: 'quizzes',
  RELATED_EPISODES: 'related episodes',
};

export const EVENT_CONSTANTS = {
  AUTO_LOAD: 'auto load',
  AUTO_PLAY: 'auto play chapter',
  BUTTON: BUTTON,
  COMPONENT: COMPONENT,
  EPISODE_IMAGE: 'episode cover image',
  EPISODE_TITLE: 'episode title',
  PAGE: PAGE,
  SECTION: SECTION,
  CLEAR_ALL: 'clear all',
  PREVIEW_END: 'preview end',
};

// Events that will be sent to Google Tag Manager
export const GTM_EVENT_ALLOWLIST = [
  EPISODE_EVENTS.BOOKMARK,
  EPISODE_EVENTS.START,
  EPISODE_EVENTS.LISTENED,
  EPISODE_EVENTS.COMPLETE,
  FREE_TRIAL_SHARE_EVENTS.SHARE.SUCCESS,
  EPISODE_SHARE_EVENTS.LINK_COPY,
  EPISODE_SHARE_EVENTS.EMAIL.SUCCESS,
  ONBOARDING_EVENTS.FINALIZE.SUCCESS,
  ...Object.values(MARKETING_EVENTS),
  ...Object.values(GA4_KEY_EVENTS),
] as const;
